import React, { useState, useEffect } from 'react';
import '../../styles/AdminDashboard.scss';
import { cmsAPI, getImageUrl } from '../../utils/api';
import { FaHome, FaSave, FaCloudUploadAlt, FaTimes, FaEye } from 'react-icons/fa';

const AdminHome = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ section_key: 'hero', title: '', subtitle: '', description: '', button_text: '', button_link: '', image: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSections = async () => {
    try {
      const res = await cmsAPI.getHome();
      if (res.data.success) {
        setSections(res.data.data);
        const hero = res.data.data.find(s => s.section_key === 'hero');
        if (hero) {
          setFormData({
            section_key: 'hero',
            title: hero.title || '', subtitle: hero.subtitle || '', description: hero.description || '',
            button_text: hero.button_text || '', button_link: hero.button_link || '', image: null
          });
          if (hero.image) setImagePreview(getImageUrl(hero.image));
        }
      }
    } catch (error) {
      console.error('Error fetching home sections:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('subtitle', formData.subtitle);
    data.append('description', formData.description);
    data.append('button_text', formData.button_text);
    data.append('button_link', formData.button_link);
    if (formData.image) data.append('image', formData.image);

    try {
      const res = await cmsAPI.updateHome(formData.section_key, data);
      if (res.data.success) {
        fetchSections();
        alert('Home section updated successfully!');
      } else {
        alert('Failed to update home section');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('An error occurred while saving.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-dashboard-page">
      <div className="dashboard-header">
        <h1>CMS - Home Page</h1>
        <p>Customize the landing page content and hero section</p>
      </div>

      <div className="admin-card">
        <h3><FaHome /> Edit Hero Section</h3>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>Hero Title</label>
            <input 
              type="text" placeholder="The main headline on your homepage"
              value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Subtitle</label>
            <input 
              type="text" placeholder="Secondary text below the title"
              value={formData.subtitle} onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea 
              placeholder="Short introductory text..." rows="3"
              value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label>Button Text</label>
              <input type="text" placeholder="e.g. Get Started" value={formData.button_text} onChange={(e) => setFormData({...formData, button_text: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Button Link</label>
              <input type="text" placeholder="e.g. /services" value={formData.button_link} onChange={(e) => setFormData({...formData, button_link: e.target.value})} />
            </div>
          </div>

          <div className="form-group">
            <label>Hero Background Image</label>
            <div className="file-input-wrapper">
              <FaCloudUploadAlt size={30} color="#4318ff" />
              <p style={{ margin: '10px 0 0 0', color: '#a3aed0', fontSize: '14px' }}>
                {formData.image ? formData.image.name : 'Choose a high-quality background image'}
              </p>
              <input type="file" accept="image/*" onChange={handleImageChange} />
              
              {imagePreview && (
                <div className="file-preview">
                  <img src={imagePreview} alt="Hero Preview" />
                  <div className="remove-file" onClick={(e) => { e.stopPropagation(); clearImage(); }}>
                    <FaTimes />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              <FaSave /> {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h3><FaEye /> Live Preview State</h3>
        <div style={{ padding: '10px' }}>
          {loading ? <p>Loading current state...</p> : sections.map(section => (
            <div key={section.id} className="item-card" style={{ width: '100%', maxWidth: 'none' }}>
              <div style={{ padding: '20px' }}>
                <h2 style={{ color: '#1b2559', margin: '0 0 10px 0' }}>{section.title || 'No Title Set'}</h2>
                <h4 style={{ color: '#4318ff', margin: '0 0 15px 0', fontWeight: 500 }}>{section.subtitle}</h4>
                <p style={{ color: '#a3aed0', lineHeight: 1.6 }}>{section.description}</p>
                <div style={{ marginTop: '20px', display: 'flex', gap: '15px' }}>
                  {section.button_text && (
                    <button className="btn-primary" style={{ pointerEvents: 'none' }}>{section.button_text}</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
